import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';

interface MultiAssigneeInputProps {
  assignees: string[];
  onChange: (assignees: string[]) => void;
  suggestions: string[];
  suggestionGroups?: Array<{ label: string; suggestions: string[] }>;
  className?: string;
  labelForAssignee?: (assignee: string) => string;
  placeholder?: string;
  allowCustomValues?: boolean;
  emptySuggestionsMessage?: string;
  disabled?: boolean;
  /** Associate an external <label htmlFor> with the text input. */
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const MultiAssigneeInput: React.FC<MultiAssigneeInputProps> = ({ 
  assignees, 
  onChange, 
  suggestions, 
  suggestionGroups,
  className,
  labelForAssignee,
  placeholder,
  allowCustomValues = true,
  emptySuggestionsMessage,
  disabled = false,
  id,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const { theme } = useTheme();
  const { t } = useSettings();
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedPlaceholder = placeholder || t('Enter assignee names, separated by commas');
  // The placeholder disappears once a chip exists, so the input keeps its name
  // from the associated label or, without one, from the placeholder text.
  const inputAriaLabel = ariaLabel ?? (id || ariaLabelledBy ? undefined : resolvedPlaceholder);
  const baseSuggestions = useMemo(
    () => (suggestionGroups && suggestionGroups.length > 0
      ? suggestionGroups.flatMap((group) => group.suggestions)
      : suggestions),
    [suggestionGroups, suggestions]
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowSuggestions(true);
    setSelectedIndex(0);
  };

  const closeSuggestions = () => {
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showSuggestions) {
        setShowSuggestions(true);
        setSelectedIndex(filteredSuggestions.length > 0 ? 0 : -1);
        return;
      }
      if (filteredSuggestions.length > 0) {
        setSelectedIndex((prev) => (prev + 1) % filteredSuggestions.length);
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showSuggestions) {
        setShowSuggestions(true);
        setSelectedIndex(filteredSuggestions.length > 0 ? filteredSuggestions.length - 1 : -1);
        return;
      }
      if (filteredSuggestions.length > 0) {
        setSelectedIndex((prev) => (prev <= 0 ? filteredSuggestions.length - 1 : prev - 1));
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      closeSuggestions();
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && filteredSuggestions[selectedIndex]) {
        addAssignee(filteredSuggestions[selectedIndex]);
        return;
      }
      if (allowCustomValues) {
        addAssignee(inputValue.trim());
      }
      return;
    }

    if (e.key === ',') {
      if (!allowCustomValues) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      addAssignee(inputValue.trim());
      return;
    }

    if (e.key === 'Backspace' && inputValue === '' && assignees.length > 0) {
      // Remove last assignee if input is empty and backspace is pressed
      removeAssignee(assignees.length - 1);
    }
  };

  const addAssignee = (assignee: string) => {
    const normalized = assignee.trim();
    if (!normalized) {
      return;
    }

    if (!allowCustomValues && !baseSuggestions.includes(normalized)) {
      return;
    }

    if (!assignees.includes(normalized)) {
      onChange([...assignees, normalized]);
    }
    setInputValue('');
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const removeAssignee = (index: number) => {
    const newAssignees = assignees.filter((_, i) => i !== index);
    onChange(newAssignees);
  };

  const selectSuggestion = (suggestion: string) => {
    addAssignee(suggestion);
    inputRef.current?.focus();
  };

  const filteredSuggestions = useMemo(() => {
    const normalizedQuery = inputValue.trim().toLowerCase();

    return baseSuggestions.filter(s => {
      if (assignees.includes(s)) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      return (
        s.toLowerCase().includes(normalizedQuery) ||
        (labelForAssignee?.(s) || '').toLowerCase().includes(normalizedQuery)
      );
    });
  }, [assignees, baseSuggestions, inputValue, labelForAssignee]);

  const filteredSuggestionGroups = useMemo(() => {
    if (!suggestionGroups || suggestionGroups.length === 0) {
      return [];
    }

    const filteredSet = new Set(filteredSuggestions);
    return suggestionGroups
      .map((group) => ({
        label: group.label,
        suggestions: group.suggestions.filter((suggestion) => filteredSet.has(suggestion)),
      }))
      .filter((group) => group.suggestions.length > 0);
  }, [filteredSuggestions, suggestionGroups]);

  useEffect(() => {
    if (!showSuggestions) {
      setSelectedIndex(-1);
      return;
    }

    if (filteredSuggestions.length === 0) {
      setSelectedIndex(-1);
      return;
    }

    setSelectedIndex((prev) => {
      if (prev < 0) {
        return 0;
      }
      if (prev >= filteredSuggestions.length) {
        return filteredSuggestions.length - 1;
      }
      return prev;
    });
  }, [filteredSuggestions.length, showSuggestions]);

  const canShowEmptyState = showSuggestions && filteredSuggestions.length === 0 && !!emptySuggestionsMessage;
  const suggestionsOpen = showSuggestions && (filteredSuggestions.length > 0 || canShowEmptyState);

  // Claim Escape above the hosting panel while the list is open so the first
  // press closes only the list (the manager stops Escape before onKeyDown).
  useModalEscape('assignee-suggestions', closeSuggestions, {
    priority: 400,
    enabled: suggestionsOpen,
  });

  return (
    <div className="relative">
      <div className={cn(
        "min-h-[40px] border rounded-lg p-2 flex flex-wrap gap-1 items-center",
        theme.bgSecondary,
        theme.borderPrimary,
        disabled
          ? "opacity-70"
          : "focus-within:ring-2 focus-within:ring-zinc-300/80 dark:focus-within:ring-zinc-600/70",
        className
      )}>
        {assignees.map((assignee, index) => {
          const assigneeLabel = labelForAssignee?.(assignee) || assignee;
          return (
            <div
              key={index}
              className={cn(
                "flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium",
                theme.bgTertiary,
                theme.textPrimary,
                theme.borderPrimary,
                "border"
              )}
            >
              <span>{assigneeLabel}</span>
              <button
                type="button"
                onClick={() => removeAssignee(index)}
                disabled={disabled}
                aria-label={t('Remove {{name}}', { name: assigneeLabel })}
                className={cn("rounded-sm p-0.5", theme.surfaceHover)}
              >
                <X size={12} />
              </button>
            </div>
          );
        })}
        <input
          ref={inputRef}
          id={id}
          type="text"
          value={inputValue}
          disabled={disabled}
          aria-label={inputAriaLabel}
          aria-labelledby={ariaLabelledBy}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onFocus={() => {
            if (!disabled) {
              setShowSuggestions(true);
            }
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          placeholder={assignees.length === 0 ? resolvedPlaceholder : ""}
          className={cn(
            "flex-1 min-w-[100px] bg-transparent outline-none text-sm",
            theme.textPrimary
          )}
        />
        {!disabled ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setShowSuggestions((current) => !current);
              if (!showSuggestions) {
                inputRef.current?.focus();
              }
            }}
            className={cn("rounded-md p-1", theme.surfaceHover, theme.textSecondary)}
            aria-label={t('Toggle suggestions')}
          >
            <ChevronDown
              size={14}
              className={cn("transition-transform", showSuggestions && "rotate-180")}
            />
          </button>
        ) : null}
      </div>
      
      {/* Suggestions dropdown */}
      {suggestionsOpen && (
        <div className={cn(
          "absolute top-full left-0 right-0 mt-1 border rounded-lg shadow-lg z-50 max-h-56 overflow-y-auto",
          theme.bgPrimary,
          theme.borderPrimary
        )}>
          {filteredSuggestionGroups.length > 0 ? (
            filteredSuggestionGroups.map((group) => (
              <div key={group.label}>
                <div className={cn("px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide", theme.textMuted)}>
                  {group.label}
                </div>
                {group.suggestions.map((suggestion) => {
                  const index = filteredSuggestions.indexOf(suggestion);
                  return (
                    <button
                      key={suggestion}
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => selectSuggestion(suggestion)}
                      className={cn(
                        "w-full text-left px-3 py-2 text-sm transition-colors",
                        index === selectedIndex
                          ? cn(theme.surfaceSelected, theme.selectionRing, theme.textPrimary)
                          : cn(theme.hover, theme.textPrimary)
                      )}
                    >
                      {labelForAssignee?.(suggestion) || suggestion}
                    </button>
                  );
                })}
              </div>
            ))
          ) : filteredSuggestions.map((suggestion, index) => (
            <button
              key={suggestion}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectSuggestion(suggestion)}
              className={cn(
                "w-full text-left px-3 py-2 text-sm transition-colors",
                index === selectedIndex
                  ? cn(theme.surfaceSelected, theme.selectionRing, theme.textPrimary)
                  : cn(theme.hover, theme.textPrimary)
              )}
            >
              {labelForAssignee?.(suggestion) || suggestion}
            </button>
          ))}
          {canShowEmptyState ? (
            <div className={cn("px-3 py-2 text-sm", theme.textSecondary)}>
              {emptySuggestionsMessage}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
